import { AdvancedSelect } from '../../widgets/select';
import { Link } from '../../widgets/buttons';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { graphql, useFragment } from 'react-relay/hooks';
import Loader from 'src/components/widgets/loader';
import { T, isMobile } from 'src/shared/helpers';
import styled from 'styled-components';
import { DesignMainOutputFiles_urls$key } from './__generated__/DesignMainOutputFiles_urls.graphql';
import OrderErrorRendering from '../orders/orderErrorRendering';

const DownloadContainer = styled.div<{ $hasBottomMargin?: boolean }>`
    ${isMobile()
        ? `
            display:block;
            grid-template-columns: 40% auto;
            margin-top:20px;
            margin-bottom: 20px;
        `
        : `   margin-top:15px;
            flex-direction: column;
            justify-content: flex-start;
            gap: 10px;
            align-items: flex-start;
            flex-wrap:wrap;
        `}
    ${(props) => props.$hasBottomMargin && `margin-bottom: 20px;`}
`;

const DownloadButtons = styled.div`
    width: 100%;
    display: grid;
    justify-content: left;
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
`;

const DonwnloadFilesDescription = styled.label<{ $isLink: boolean }>`
    align-self: center;
    ${isMobile() ? 'margin-left: 10px;' : 'margin-left: 5px;'}
    margin-top: 12px;
    margin-bottom: 12px;
    display: block;
    color: ${(props) => (props.$isLink ? '#f46200' : '#445a64')};
    cursor: ${(props) => (props.$isLink ? 'pointer' : 'default')};
    font-weight: ${(props) => (props.$isLink ? 'bold' : 'normal')};
`;

const DownloadButton = styled.button`
    display: inline-flex !important;
    flex-direction: column;
    border: 1px solid #8fa4ae !important;
    border-radius: 8px !important;
    height: 45px !important;
    width: 45px !important;
    background: white !important;
    display: inline-grid;
    justify-content: center;
    align-items: center;
    vertical-align: middle;
    margin: 5px;
    padding: 0px !important;

    label {
        font-size: 12px;
        text-align: center;
        text-decoration: none !important;
        color: black;
        cursor: pointer;

        &:hover {
            color: black !important;
        }
    }

    &:hover {
        cursor: pointer;
        background: #dfe3e8;
    }
`;

export const Center = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AreaSelect = styled(AdvancedSelect)`
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 5px;
    width: max-content !important;
    width: 100%;
    ${isMobile() ? 'margin-right: 20px' : ''}
`;

const SideLabelForSelect = styled.label`
    color: black;
    display: grid;
    ${isMobile() ? 'margin-left: 10px;' : 'margin-left: 5px;'}
    font-size: 14px;
    padding-top: 10px;
    padding-bottom: 10px;
`;

const MulticanvasLabel = styled.label`
    color: black;
`;
const MulticanvasLabelContainer = styled.div`
    margin-bottom: 20px;
    ${isMobile() ? 'margin-left: 10px;' : 'margin-left: 5px;'}
`;

interface OptionObject {
    label: string;
    value: number;
}

interface Props {
    designQuery: DesignMainOutputFiles_urls$key;
    notReadyZip?: boolean;
    countOutputFiles?: (mainOutputFilesUrls: any) => void;
    designModificationID?: string | null;
    onBulkVariantNotMatched?: (notMatched: boolean) => void;
}

interface SelectedAreaForSide {
    [propName: string]: number;
}

const DesignMainOutputFiles: FunctionComponent<Props> = ({ designQuery, notReadyZip, countOutputFiles, designModificationID, onBulkVariantNotMatched }) => {
    const [isPending, setIsPending] = useState<string>();

    const query = useFragment<DesignMainOutputFiles_urls$key>(
        graphql`
            fragment DesignMainOutputFiles_urls on Design {
                mainOutputFilesUrls {
                    sideID
                    sideName
                    areaID
                    format
                    url
                    row
                    column
                    modificationID
                    isPreview
                }
                modifications {
                    modificationID
                    variantNotMatched
                }
                # Proviamo a leggere il modello con il campo "name" confermato
                model {
                    id
                    colors {
                        sides {
                            sideID
                            areas {
                                areaID
                                name # <--- Questo è il campo che abbiamo visto in AreaDesigner.tsx
                            }
                        }
                    }
                }
            }
        `,
        designQuery
    );
    const mainOutputFilesUrls = query.mainOutputFilesUrls;

    const hasBulkVariantNotMatched = useMemo(() => {
        if (!designModificationID) return false;
        return (query.modifications ?? []).some((m) => m?.modificationID === designModificationID && m?.variantNotMatched === true);
    }, [query.modifications, designModificationID]);

    useEffect(() => {
        if (countOutputFiles) countOutputFiles(mainOutputFilesUrls);
    }, [mainOutputFilesUrls]);

    useEffect(() => {
        onBulkVariantNotMatched?.(hasBulkVariantNotMatched);
    }, [hasBulkVariantNotMatched, onBulkVariantNotMatched]);

    const uniqueSides = [
        ...new Set(
            mainOutputFilesUrls
                .map((el) => [el.sideID, el.sideName] as [number, string])
                .sort((a, b) => (a[0] as number) - (b[0] as number))
                .map((e) => e[1])
        )
    ];

    function getAreasFromSide(singleSideName: string | null) {
        return [...new Set(mainOutputFilesUrls.filter((el) => el.sideName == singleSideName).map((e) => e.areaID))];
    }

    function getAllAreasForSelect(singleSideName: string | null) {
        let map = [...new Set(mainOutputFilesUrls.filter((el) => el.sideName == singleSideName).map((e) => e.areaID))];

        return useMemo(
            () =>
                map.map((areaID) => {
                    let foundName = '';

                    // Controllo di sicurezza: Esiste il modello?
                    if (query.model && query.model.colors) {
                        const currentFile = mainOutputFilesUrls.find((f) => f.sideName === singleSideName);

                        // Iteriamo sui colori
                        for (const color of query.model.colors) {
                            if (color && color.sides) {
                                // Cerchiamo il lato
                                const side = color.sides.find((s) => s && s.sideID === currentFile?.sideID);

                                if (side && side.areas) {
                                    // Cerchiamo l'area
                                    const area = side.areas.find((a) => a && a.areaID === areaID);

                                    if (area && area.name) {
                                        foundName = area.name;
                                        console.log('✅ TROVATO NOME REALE:', foundName);
                                        break;
                                    }
                                }
                            }
                        }
                    }

                    // Se non troviamo il nome, fallback
                    const labelName = foundName ? foundName : T._('Area', 'Admin') + ' ' + areaID.toString();

                    return {
                        label: labelName,
                        value: areaID
                    };
                }),
            [map, query.model, singleSideName]
        );
    }

    function changeSelectedArea(objOpt: OptionObject | null, key: string | null) {
        if (objOpt != null && key != null) {
            const selectedAreaForSideCopy = { ...selectedAreaForSide };
            selectedAreaForSideCopy[key] = objOpt.value;
            setSelectedAreaForSide(selectedAreaForSideCopy);
        }
    }

    const [selectedAreaForSide, setSelectedAreaForSide] = useState<SelectedAreaForSide>({});
    const [selectedPageForSingle, setSelectedPageForSingle] = useState<string>();

    // --- FUNZIONI STANDARD (Download, PRFLabel, ecc.) - Nessuna modifica ---
    function forceDownload(blob: string, filename: string) {
        var a = document.createElement('a');
        a.download = filename;
        a.href = blob;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    function downloadResource(url: string, side: string) {
        let filename = url.split('\\')?.pop()?.split('/').pop()!;
        if (filename != null) {
            setIsPending(side);
            fetch(url, { headers: new Headers({ Origin: location.origin }), mode: 'cors' })
                .then((response) => response.blob())
                .then((blob) => {
                    let blobUrl = window.URL.createObjectURL(blob);
                    forceDownload(blobUrl, filename);
                    setIsPending(undefined);
                })
                .catch((e) => {
                    setIsPending(undefined);
                    console.error(e);
                });
        }
    }

    function getPRFLabel(url: string | null): React.ReactNode {
        let extension = url?.match('[^.]+$')?.toString();
        let res = '';
        switch (extension) {
            case 'svg':
                res = 'SVG';
                break;
            case 'png':
                res = 'PNG';
                break;
            case 'pdf':
                res = url?.includes('effect_vector.pdf') ? 'PDF Eff. Vctr' : 'PDF';
                break;
            case 'dxf':
                res = url?.includes('HQ.dxf') ? 'DXF HQ' : 'DXF';
                break;
            case 'btm':
                res = 'BTM';
                break;
            case 'bmp':
                res = 'BMP';
                break;
            case 'ai':
                res = 'AI';
                break;
            default:
                break;
        }
        if (url?.includes('effect') && !res.includes('Eff.')) res += ' effect';
        if (url?.includes('missing') && res.includes('PNG')) res += ' missing';
        return res;
    }

    function groupByRowAndColumnOutputFiles(singleSideName: string, outputFilesUrls: any[]): React.ReactNode {
        let reduced = outputFilesUrls.reduce((accumulator: any, current: any) => {
            let itemIdx = accumulator.findIndex((it: any) => it.row === current.row && it.column === current.column);
            if (itemIdx === -1) {
                return [...accumulator, { row: current.row, column: current.column, mainOutputFilesUrls: [{ ...current }] }].sort(
                    (a, b) => a.row - b.row || a.column - b.column
                );
            } else {
                let newItem = { ...accumulator[itemIdx], mainOutputFilesUrls: [...accumulator[itemIdx].mainOutputFilesUrls, { ...current }] };
                let arrCopy = [...accumulator];
                arrCopy.splice(itemIdx, 1, newItem);
                return arrCopy;
            }
        }, []);
        if (reduced) {
            return reduced.map((rowAndColumn: any) => (
                <MulticanvasLabelContainer key={`${rowAndColumn.row}-${rowAndColumn.column}`}>
                    <MulticanvasLabel>
                        <strong>{T._('Row: ', 'Admin')}</strong>
                        {rowAndColumn.row},
                    </MulticanvasLabel>
                    <MulticanvasLabel>
                        <strong>{T._(' Column: ', 'Admin')}</strong>
                        {rowAndColumn.column}
                    </MulticanvasLabel>
                    <DownloadButtons>
                        {rowAndColumn.mainOutputFilesUrls.map((dfu: any) => (
                            <DownloadButton key={dfu.format} onClick={() => downloadResource(dfu.url ?? '', singleSideName ?? '')}>
                                <label>{getPRFLabel(dfu.url)}</label>
                            </DownloadButton>
                        ))}
                    </DownloadButtons>
                </MulticanvasLabelContainer>
            ));
        } else return <></>;
    }

    function getPage(url: string) {
        return (
            url
                .match(/(?:page_)[0-9]+?(?=[^_\w]*)/g)
                ?.toString()
                ?.split('_')
                .at(1) ?? ''
        );
    }

    function getAllPagesForSelect() {
        let map = mainOutputFilesUrls.filter((el) => !el.url?.includes('SINGLE')).map((e) => e.url);
        let pagesArray = new Array();
        for (let url of map) {
            if (url != null && getPage(url) != undefined && !pagesArray.includes(getPage(url))) pagesArray.push(getPage(url));
        }
        return useMemo(() => pagesArray.map((x) => ({ label: T._('Page', 'Admin') + ' ' + x.toString(), value: x })), [map]);
    }

    function changeSelectedPage(objOpt: OptionObject | null) {
        if (objOpt != null) setSelectedPageForSingle(objOpt.value.toString());
    }

    function sideHasMissingPng(singleSideName: string) {
        const areas = getAreasFromSide(singleSideName);
        if (areas.length === 1) {
            return mainOutputFilesUrls.some((dfu) => dfu.sideName === singleSideName && dfu.url?.includes('missing_png'));
        } else {
            const selectedArea = selectedAreaForSide[singleSideName ?? ''];
            if (selectedArea == null) return false;
            return mainOutputFilesUrls.some((dfu) => dfu.sideName === singleSideName && dfu.areaID === selectedArea && dfu.url?.includes('missing_png'));
        }
    }

    function handleSingleFiles(singleSideName: string): React.ReactNode {
        return (
            <DownloadContainer>
                {mainOutputFilesUrls
                    .filter((d) => d.url?.includes('SINGLE'))
                    .map(
                        (dfu) =>
                            dfu.url != null && (
                                <DownloadButton key={dfu.format} onClick={() => downloadResource(dfu.url ?? '', singleSideName ?? '')}>
                                    <label>{getPRFLabel(dfu.url)}</label>
                                </DownloadButton>
                            )
                    )}
                {getAllPagesForSelect().length > 0 && (
                    <SideLabelForSelect>
                        <strong>{T._('Page ', 'Admin')}</strong>
                    </SideLabelForSelect>
                )}
                {
                    <AreaSelect
                        isMulti={false}
                        isSearchable={false}
                        options={getAllPagesForSelect()}
                        placeholder={T._('Select page', 'Admin')}
                        value={getAllPagesForSelect().find((option) => (option?.value ?? 0) == selectedPageForSingle)}
                        onChange={(option) => changeSelectedPage(option as OptionObject)}
                    />
                }
                <DownloadButtons>
                    {selectedPageForSingle != undefined &&
                        mainOutputFilesUrls
                            .filter((d) => !d.url?.includes('SINGLE') && selectedPageForSingle == getPage(d.url ?? ''))
                            .map(
                                (dfu) =>
                                    dfu.url != null &&
                                    selectedPageForSingle == getPage(dfu.url) && (
                                        <DownloadButton key={dfu.url} onClick={() => downloadResource(dfu.url ?? '', singleSideName ?? '')}>
                                            <label>{getPRFLabel(dfu.url)}</label>
                                        </DownloadButton>
                                    )
                            )}
                </DownloadButtons>
            </DownloadContainer>
        );
    }

    return (
        <>
            {mainOutputFilesUrls.length > 0 && !notReadyZip && (
                <>
                    <DonwnloadFilesDescription $isLink={false}>
                        {T._(`Download individual files or a ZIP archive containing all availabe formats and the order's summary. `, 'Admin')}
                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360016291573-Orders-and-Print-Ready-Files'>
                            {T._(`Explore more`, 'Admin')}
                        </Link>
                        .
                    </DonwnloadFilesDescription>
                </>
            )}

            {mainOutputFilesUrls &&
                uniqueSides.map((singleSideName) => (
                    <React.Fragment key={singleSideName}>
                        <DownloadContainer>
                            {!mainOutputFilesUrls.find((file) => file.url?.includes('SINGLE')) && (
                                <SideLabelForSelect>
                                    <strong>{T._('Side ', 'Admin')}</strong>
                                    {singleSideName}
                                </SideLabelForSelect>
                            )}
                            {getAreasFromSide(singleSideName).length != 1 && (
                                <AreaSelect
                                    isMulti={false}
                                    isSearchable={false}
                                    options={getAllAreasForSelect(singleSideName)}
                                    placeholder={T._('Select print area', 'Admin')}
                                    value={getAllAreasForSelect(singleSideName).find(
                                        (option) => (option?.value ?? 0) == selectedAreaForSide[singleSideName ?? '']
                                    )}
                                    onChange={(option) => changeSelectedArea(option as OptionObject, singleSideName)}
                                />
                            )}
                            {isMobile() && <p></p>}
                            {/* Logica di visualizzazione bottoni (invariata) */}
                            {(getAreasFromSide(singleSideName).length == 1 &&
                                mainOutputFilesUrls.some(
                                    (dfu) => dfu.isPreview == false && (dfu.row == null || dfu.column == null) && dfu.url?.includes('SINGLE')
                                ) &&
                                handleSingleFiles(singleSideName ?? '')) ||
                                (getAreasFromSide(singleSideName).length == 1 && !mainOutputFilesUrls.some((dfu) => dfu.row != null || dfu.column != null) && (
                                    <DownloadButtons>
                                        {mainOutputFilesUrls
                                            .filter((dfu) => dfu.areaID == getAreasFromSide(singleSideName).at(0) && dfu.sideName == singleSideName)
                                            .map((dfu) => (
                                                <DownloadButton key={dfu.url} onClick={() => downloadResource(dfu.url ?? '', singleSideName ?? '')}>
                                                    <label>{getPRFLabel(dfu.url)}</label>
                                                </DownloadButton>
                                            ))}
                                    </DownloadButtons>
                                )) ||
                                (getAreasFromSide(singleSideName).length == 1 &&
                                    !mainOutputFilesUrls.some((dfu) => dfu.row == null || dfu.column == null) &&
                                    groupByRowAndColumnOutputFiles(
                                        singleSideName ?? '',
                                        mainOutputFilesUrls.filter(
                                            (dfu) => dfu.areaID == getAreasFromSide(singleSideName).at(0) && dfu.sideName == singleSideName
                                        )
                                    )) ||
                                (getAreasFromSide(singleSideName).length === 1 &&
                                    mainOutputFilesUrls.some((dfu) => dfu.row == null || dfu.column == null) &&
                                    groupByRowAndColumnOutputFiles(
                                        singleSideName ?? '',
                                        mainOutputFilesUrls.filter(
                                            (dfu) => dfu.areaID == getAreasFromSide(singleSideName).at(0) && dfu.sideName == singleSideName
                                        )
                                    ))}
                            {(getAreasFromSide(singleSideName).length > 1 &&
                                !mainOutputFilesUrls.some((dfu) => dfu.row != null || dfu.column != null) &&
                                mainOutputFilesUrls
                                    .filter((dfu) => dfu.areaID == selectedAreaForSide[singleSideName ?? ''] && dfu.sideName == singleSideName)
                                    .map((dfu) => (
                                        <DownloadButton key={dfu.url} onClick={() => downloadResource(dfu.url ?? '', singleSideName ?? '')}>
                                            <label>{getPRFLabel(dfu.url)}</label>
                                        </DownloadButton>
                                    ))) ||
                                (getAreasFromSide(singleSideName).length > 1 &&
                                    !mainOutputFilesUrls.some((dfu) => dfu.row == null || dfu.column == null) &&
                                    mainOutputFilesUrls.some(
                                        (dfu) => dfu.areaID == selectedAreaForSide[singleSideName ?? ''] && dfu.sideName == singleSideName
                                    ) &&
                                    groupByRowAndColumnOutputFiles(
                                        singleSideName ?? '',
                                        mainOutputFilesUrls.filter(
                                            (dfu) => dfu.areaID == selectedAreaForSide[singleSideName ?? ''] && dfu.sideName == singleSideName
                                        )
                                    ))}
                            {singleSideName == isPending && (
                                <Center style={{ marginTop: '15px' }}>
                                    <Loader />
                                </Center>
                            )}
                        </DownloadContainer>
                        {sideHasMissingPng(singleSideName) && (
                            <OrderErrorRendering
                                message={`The PNG file was not generated because it exceeds the 8000x8000 pixel limit.<br /><strong>If you do not need the PNG format, you can ignore this message.</strong> Otherwise, please <a href="https://zakeke.zendesk.com/hc/en-us/articles/25561156898332-PNG-Missing" target="_blank">read here</a>  on how to recover it and prevent this from happening again.`}
                                isWarningMessage={true}
                            ></OrderErrorRendering>
                        )}
                    </React.Fragment>
                ))}
        </>
    );
};

export default DesignMainOutputFiles;
